import type { LegendLabel } from '@better-lights/core';
import { LINE_COLORS, labelFor, lineColorName } from '@better-lights/core';
import { Check } from 'lucide-react';

/** Preset colors as equal stripes rotated −35°, plus a white "hot spot" (docs/spec/06 §2 Swatch). */
export function SwatchFace({ colors, size }: { colors: string[]; size: number }) {
  const n = colors.length;
  const stops = colors.map((c, i) => `${c} ${(i / n) * 100}% ${((i + 1) / n) * 100}%`).join(', ');
  return (
    <span
      aria-hidden
      className="relative inline-block shrink-0 overflow-hidden rounded-full border border-white/25"
      style={{ width: size, height: size, background: n === 1 ? colors[0] : `linear-gradient(55deg, ${stops})` }}
    >
      <span
        className="absolute rounded-full bg-white/38"
        style={{ width: size * 0.42, height: size * 0.42, top: size * 0.16, left: size * 0.2 }}
      />
    </span>
  );
}

export function Swatch({
  colors,
  size,
  selected,
  label,
  onClick,
  dashed,
}: {
  colors: string[];
  size: number;
  selected: boolean;
  label: string;
  onClick: () => void;
  dashed?: boolean;
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      aria-label={label}
      title={label}
      onClick={onClick}
      className={`flex shrink-0 items-center justify-center rounded-full border-2 ${dashed ? 'border-dashed' : ''} ${selected ? 'border-accent' : 'border-transparent'}`}
      style={{ width: size + 8, height: size + 8 }}
    >
      <SwatchFace colors={colors} size={size} />
    </button>
  );
}

/** Built-in line colors then custom ones; check mark on the selected color. */
export function LineColorPicker({
  value,
  onChange,
  custom,
  labels,
  size = 30,
  label,
}: {
  value: string;
  onChange: (hex: string) => void;
  custom: string[];
  labels: Record<string, LegendLabel>;
  size?: number;
  label: string;
}) {
  const all = [
    ...LINE_COLORS.map((c) => c.hex as string),
    ...custom.filter((c) => !LINE_COLORS.some((l) => l.hex.toLowerCase() === c.toLowerCase())),
  ];
  return (
    <div role="radiogroup" aria-label={label} className="flex gap-1 overflow-x-auto pb-1">
      {all.map((hex) => {
        const on = hex.toLowerCase() === value.toLowerCase();
        const name = lineColorName(hex);
        const legend = labelFor(labels, hex).name;
        const a11y = legend && legend !== name ? `${name}: ${legend}` : name;
        return (
          <button
            key={hex}
            type="button"
            role="radio"
            aria-checked={on}
            aria-label={a11y}
            title={a11y}
            onClick={() => onChange(hex)}
            className={`flex shrink-0 items-center justify-center rounded-full border-2 ${on ? 'border-text' : 'border-transparent'}`}
            style={{ width: size + 8, height: size + 8 }}
          >
            <span
              className="flex items-center justify-center rounded-full"
              style={{ width: size, height: size, background: hex }}
            >
              {on ? <Check size={size * 0.55} strokeWidth={3} color="#0A0B0D" aria-hidden /> : null}
            </span>
          </button>
        );
      })}
    </div>
  );
}
